"use client";

import { useEffect, useRef, useState, memo } from "react";
import { Container } from "@/components/ui/Container";
import CTAButton from "@/components/ui/CTAButton";
import { ShimmerDots } from "@/components/ShimmerDots";
import { ScrollMouseIcon } from "@/components/ScrollMouseIcon";
import { List } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";

const translations = {
  en: {
    badge: "Immediate availability",
    headline1: "YOUR WEBSITE",
    headline2: "IN 4 DAYS!",
    description: "The agency revolutionizing web design."
  },
  fr: {
    badge: "Disponibilités immédiates",
    headline1: "VOTRE SITE WEB",
    headline2: "EN 4 JOURS !",
    description: "L'agence qui révolutionne le web design."
  }
};

export const HeroSection = memo(function HeroSection() {
  const containerRef = useRef<HTMLElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const { lang } = useLanguage();
  const t = translations[lang];
  const isMobile = useIsMobile();

  // Simple scroll opacity effect - CSS only on mobile
  useEffect(() => {
    if (isMobile) return;
    
    const handleScroll = () => {
      const opacity = Math.max(0.3, 1 - window.scrollY / 400);
      setScrollOpacity(opacity);
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  // Mouse glow effect - desktop only
  useEffect(() => {
    if (isMobile) return;
    
    let rafId: number;
    let lastX = 0;
    let lastY = 0;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        lastX = e.clientX - rect.left;
        lastY = e.clientY - rect.top;
        
        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            setMousePos({ x: lastX, y: lastY });
            rafId = 0;
          });
        }
      }
    };
    
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMobile]);

  return (
    <section id="hero" ref={containerRef} className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {!isMobile && <ShimmerDots />}

      {/* Simple CSS cursor follow glow - desktop only */}
      {!isMobile && (
        <div 
          className="absolute pointer-events-none z-0 w-[400px] h-[400px] rounded-full transition-transform duration-150 ease-out"
          style={{
            transform: `translate(${mousePos.x - 200}px, ${mousePos.y - 200}px)`,
            background: "radial-gradient(circle, hsl(var(--accent-indigo) / 0.15) 0%, transparent 60%)"
          }}
        />
      )}

      <Container className="relative z-10">
        <div
          className="max-w-3xl mx-auto text-center animate-fade-in"
          style={{ opacity: isMobile ? 1 : scrollOpacity }}
        >
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm mb-6 animate-scale-in"
            style={{ animationDelay: '0.1s', animationFillMode: 'both' }}
          >
            <List size={14} />
            <span>{t.badge}</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight leading-[1.1] mb-6">
            <span className="text-foreground mx-0 px-0 inline-block text-center font-semibold">
              {t.headline1}
            </span>
            <br />
            <span className="gradient-text inline-block">
              {t.headline2}
            </span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
            {t.description}
          </p>

          {/* CTA */}
          <div
            className="flex justify-center animate-fade-in"
            style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
          >
            <CTAButton />
          </div>
        </div>
      </Container>

      {/* Scroll indicator */}
      <ScrollMouseIcon />
    </section>
  );
});
